import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import Postcard from "@/components/Postcard";
import { useAuth } from "@/context/AuthContext";

const TRAVEL_STYLES = ["Solo", "Couple", "Family", "Group"];
const BUDGETS = [
  { label: "Budget (₹)", value: "₹" },
  { label: "Mid-range (₹₹)", value: "₹₹" },
  { label: "Luxury (₹₹₹)", value: "₹₹₹" },
];
const REGIONS = ["Asia", "Europe", "Americas", "Africa", "Middle East"];

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join(", ");
  return fallback;
};

export default function Auth() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "signin" ? "signin" : "signup";
  const next = params.get("next");
  const { user, login, register, savePreferences } = useAuth();

  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1); // 1 = creds, 3 = prefs (no step 2 for now)
  const [submitting, setSubmitting] = useState(false);

  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [secretCode, setSecretCode] = useState("");

  const [travelStyle, setTravelStyle] = useState([]);
  const [budgetPref, setBudgetPref] = useState(null);
  const [regions, setRegions] = useState([]);

  useEffect(() => {
    // If already authenticated when landing on /auth, go back to where they came from
    // (or explore/seller dashboard). Skip when we're mid-onboarding (step 3) so freshly
    // signed-up users can finish prefs.
    if (user && step === 1) {
      navigate(next || (user.is_seller ? "/seller" : "/explore"), { replace: true });
    }
  }, [user, step, navigate, next]);

  useEffect(() => {
    // Surfaced by AuthCallback when Google sign-in is blocked (e.g. invite-only gate).
    if (params.get("error") === "oauth") {
      const detail = params.get("detail");
      if (detail && detail.toLowerCase().includes("access code")) {
        toast.error(detail, {
          action: { label: "Request access", onClick: () => navigate("/request-access") },
        });
      } else {
        toast.error(detail || "Google sign-in failed. Please try again.");
      }
    }
  }, []); // eslint-disable-line

  const toggle = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/explore";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSignin = async (e) => {
    e?.preventDefault();
    if (!email || !password) {
      toast.error("Email and password required");
      return;
    }
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back.");
      navigate(next || (u?.is_seller ? "/seller" : "/explore"));
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignupStep1 = async (e) => {
    e?.preventDefault();
    if (!email || !password || !name) {
      toast.error("Name, email and password required");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!secretCode) {
      toast.error("Access code required", {
        action: { label: "Request access", onClick: () => navigate("/request-access") },
      });
      return;
    }
    setSubmitting(true);
    try {
      setStep(3); // advance immediately so the [user]→navigate effect doesn't kick us out
      await register({ email, password, name, avatar_url: avatarUrl || null, secret_code: secretCode });
      toast.success("Account created.");
    } catch (err) {
      setStep(1);
      if (err.response?.status === 403) {
        toast.error(errMsg(err, "You need an access code to join."), {
          action: { label: "Request access", onClick: () => navigate("/request-access") },
        });
      } else {
        toast.error(errMsg(err, "Signup failed"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrefs = async () => {
    setSubmitting(true);
    try {
      await savePreferences({
        travel_style: travelStyle,
        budget_pref: budgetPref,
        regions,
      });
      toast.success("Library ready. Welcome to Nirno.");
      navigate(next || "/explore");
    } catch {
      toast.error("Could not save preferences");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="auth-page">
      {/* LEFT — tight editorial layout */}
      <div className="hidden lg:flex flex-col bg-[var(--ink)] text-[#FBF7F2] p-12 w-[42%] relative overflow-hidden">
        <Logo to="/" inverse size={32} />
        <div className="mt-12">
          <span className="eyebrow" style={{ color: "rgba(255,255,255,0.6)" }}>◆ Welcome traveller</span>
          <h2 className="h-display mt-4 text-[44px] text-white leading-[1] max-w-[400px]">
            Pack your library.<br />Arrive prepared.
          </h2>
          <p className="mt-5 text-[15px] leading-[1.6] text-[#cbb9a8] max-w-[400px]">
            Save your purchased routes, build your own, follow creators you trust. Free to join. Free forever.
          </p>
          <div className="mt-5 flex gap-3 mono text-[10px] tracking-[0.15em] uppercase text-[#a89684]">
            <span>★ 18,400 travellers</span><span>·</span><span>★ 47 creators</span>
          </div>
        </div>
        <div className="mt-auto pt-8 max-w-[340px]">
          <Postcard />
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex-1 flex items-start lg:items-center justify-center px-6 py-10 sm:py-16">
        <div className="w-full max-w-[440px]">
          <div className="lg:hidden mb-8"><Logo /></div>

          {mode === "signup" && (
            <div className="flex gap-2 mb-6" data-testid="step-indicator">
              <span className={`step-dot ${step === 1 ? "active" : "done"}`} />
              <span className={`step-dot ${step === 3 ? "active" : ""}`} />
            </div>
          )}

          {(mode === "signin" || step === 1) && (
            <>
              <span className="eyebrow">◆ {mode === "signin" ? "Welcome back" : "Get started"}</span>
              <h2 className="h-display text-[36px] mt-2 mb-6">
                {mode === "signin" ? "Open your library." : "Start your library."}
              </h2>

              <div
                className="flex border-[1.5px] border-[var(--line)] rounded-full p-1 mb-6 max-w-fit"
                data-testid="auth-tabs"
              >
                <button
                  onClick={() => setMode("signin")}
                  data-testid="tab-signin"
                  className={`px-5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${mode === "signin" ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)]"}`}
                >Sign in</button>
                <button
                  onClick={() => setMode("signup")}
                  data-testid="tab-signup"
                  className={`px-5 py-1.5 rounded-full text-[13px] font-semibold transition-colors ${mode === "signup" ? "bg-[var(--ink)] text-white" : "text-[var(--ink2)]"}`}
                >Sign up</button>
              </div>

              <form onSubmit={mode === "signin" ? handleSignin : handleSignupStep1}>
                {mode === "signup" && (
                  <>
                    <div className="inp-wrap">
                      <label className="lbl">Full name</label>
                      <input
                        className="inp"
                        placeholder="Arjun Mehra"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="input-name"
                      />
                    </div>
                    <div className="inp-wrap">
                      <label className="lbl">Profile photo URL <span className="text-[var(--ink3)]">(optional)</span></label>
                      <input
                        className="inp"
                        placeholder="https://…"
                        value={avatarUrl}
                        onChange={(e) => setAvatarUrl(e.target.value)}
                        data-testid="input-avatar"
                      />
                    </div>
                  </>
                )}
                <div className="inp-wrap">
                  <label className="lbl">Email</label>
                  <input
                    className="inp"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="input-email"
                    type="email"
                    autoComplete="email"
                  />
                </div>
                <div className="inp-wrap">
                  <label className="lbl">Password</label>
                  <input
                    className="inp"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="input-password"
                    autoComplete={mode === "signup" ? "new-password" : "current-password"}
                  />
                </div>
                {mode === "signup" && (
                  <div className="inp-wrap">
                    <label className="lbl">Access code</label>
                    <input
                      className="inp"
                      placeholder="6-digit code"
                      value={secretCode}
                      onChange={(e) => setSecretCode(e.target.value)}
                      data-testid="input-secret-code"
                      maxLength={6}
                    />
                    <Link to="/request-access" data-testid="link-request-access" className="block mt-1.5 text-[12px] text-[var(--ink3)] hover:text-[var(--red)]">
                      Don't have a code? Request access →
                    </Link>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="auth-submit"
                  className="btn btn-solid btn-lg full mt-3"
                >
                  {submitting ? "…" : mode === "signin" ? "Sign in →" : "Create account →"}
                </button>
              </form>

              <div className="flex items-center gap-3 my-5 text-[11px] mono uppercase tracking-[0.15em] text-[var(--ink3)]">
                <div className="flex-1 border-t border-[var(--line)]" />
                or
                <div className="flex-1 border-t border-[var(--line)]" />
              </div>

              <button
                onClick={handleGoogle}
                data-testid="google-auth-button"
                className="btn btn-paper full"
              >
                <svg width="16" height="16" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.5-4.6 2.5-7.5 2.5-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.5 5.5C41.4 35.6 44 30.2 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
                Continue with Google
              </button>
            </>
          )}

          {mode === "signup" && step === 3 && (
            <div data-testid="onboarding-prefs">
              <span className="eyebrow">◆ Personalise your feed</span>
              <h2 className="h-display text-[36px] mt-2 mb-2">How do you travel?</h2>
              <p className="text-[14px] text-[var(--ink2)] mb-6">
                Helps us show you the routes you'll actually take.
              </p>

              <div className="inp-wrap">
                <label className="lbl">Travel style</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {TRAVEL_STYLES.map((t) => (
                    <span
                      key={t}
                      onClick={() => toggle(travelStyle, setTravelStyle, t)}
                      data-testid={`pref-style-${t}`}
                      className={`pref-chip ${travelStyle.includes(t) ? "sel" : ""}`}
                    >{t}</span>
                  ))}
                </div>
              </div>

              <div className="inp-wrap mt-4">
                <label className="lbl">Budget preference</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {BUDGETS.map((b) => (
                    <span
                      key={b.value}
                      onClick={() => setBudgetPref(budgetPref === b.value ? null : b.value)}
                      data-testid={`pref-budget-${b.value}`}
                      className={`pref-chip ${budgetPref === b.value ? "sel" : ""}`}
                    >{b.label}</span>
                  ))}
                </div>
              </div>

              <div className="inp-wrap mt-4">
                <label className="lbl">Regions of interest</label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {REGIONS.map((r) => (
                    <span
                      key={r}
                      onClick={() => toggle(regions, setRegions, r)}
                      data-testid={`pref-region-${r}`}
                      className={`pref-chip ${regions.includes(r) ? "sel" : ""}`}
                    >{r}</span>
                  ))}
                </div>
              </div>

              <button
                disabled={submitting}
                onClick={handlePrefs}
                data-testid="prefs-continue"
                className="btn btn-solid btn-lg full mt-6"
              >
                {submitting ? "…" : "Continue →"}
              </button>
              <button
                disabled={submitting}
                onClick={() => navigate(next || "/explore")}
                data-testid="prefs-skip"
                className="btn btn-ghost btn-sm full mt-2"
              >
                Skip for now
              </button>
            </div>
          )}

          <div className="mt-8 text-center text-[12px] text-[var(--ink3)]">
            <Link to="/" className="hover:text-[var(--ink)]">← Back to home</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
